import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchMe } from "../../store/slices/authSlice";
import { fetchWallets } from "../../store/slices/tradeSlice";
import { Card, Badge, Button } from "../../components/ui/Button";
import { Input, Alert } from "../../components/ui/Modal";
import { gs } from "../../styles/globalStyles";
import { formatUSD, formatDate } from "../../utils/formatters";
import { Ico } from "../../components/ui/Icon";
import UserAvatar from "../../components/ui/UserAvatar";
import api from "../../services/api";
import toast from "react-hot-toast";

const profileSchema = z.object({
  first_name: z.string().min(2, "At least 2 characters"),
  last_name: z.string().min(2, "At least 2 characters"),
  email: z.string().email("Invalid email"),
  username: z
    .string()
    .min(3, "At least 3 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Letters, numbers, underscores only"),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Required"),
    password: z.string().min(8, "At least 8 characters"),
    password_confirmation: z.string(),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: "Passwords don't match",
    path: ["password_confirmation"],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const TABS = [
  { id: "info", label: "Profile", icon: "user" },
  { id: "security", label: "Security", icon: "lock" },
  { id: "wallets", label: "Wallets", icon: "wallet" },
] as const;

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { wallets } = useAppSelector((s) => s.trade);
  const [tab, setTab] = useState<"info" | "security" | "wallets">("info");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      username: user?.username || "",
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    dispatch(fetchWallets());
  }, [dispatch]);

  useEffect(() => {
    if (user)
      profileForm.reset({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        username: user.username,
      });
  }, [user?.id]);

  const saveProfile = async (data: ProfileForm) => {
    setSavingProfile(true);
    try {
      try {
        await api.put("/profile", data);
      } catch {
        await api.put("/user", data);
      }
      dispatch(fetchMe());
      toast.success("Profile updated!");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (data: PasswordForm) => {
    setSavingPassword(true);
    try {
      try {
        await api.put("/profile/password", data);
      } catch {
        await api.put("/auth/password", data);
      }
      toast.success("Password changed!");
      passwordForm.reset();
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append("avatar", file);
      await api.post("/profile/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(fetchMe());
      toast.success("Avatar updated!");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to upload avatar");
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await api.delete("/profile/avatar");
      dispatch(fetchMe());
      toast.success("Avatar removed");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to remove avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 w-full">
      {/* ── Avatar header card ────────────────────────────────────────── */}
      <Card className="flex items-center gap-5 mb-5">
        {/* Avatar with upload overlay on hover */}
        <div className="relative shrink-0 group">
          <UserAvatar
            user={user}
            size={64}
            className="rounded-2xl border-2 border-primary/30 shadow-glow"
          />
          <button
            type="button"
            disabled={uploadingAvatar}
            onClick={() => avatarInputRef.current?.click()}
            className="absolute inset-0 rounded-2xl bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
            title="Change avatar"
          >
            {uploadingAvatar ? (
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Ico name="camera" className="w-5 h-5 text-white" />
            )}
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarChange}
          />
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-white text-xl truncate">
            {user.first_name} {user.last_name}
          </h2>
          <p className="text-gray-400 text-sm">@{user.username}</p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Badge variant={user.role === "admin" ? "warning" : "info"}>
              {user.role.toUpperCase()}
            </Badge>
            <Badge variant={user.status === "active" ? "success" : "error"}>
              {user.status.toUpperCase()}
            </Badge>
            <Badge
              variant={user.kyc?.status === "approved" ? "success" : "warning"}
            >
              KYC: {user.kyc?.status || "not submitted"}
            </Badge>
          </div>
        </div>

        <div className="text-right hidden sm:block shrink-0">
          <p className="text-gray-500 text-xs">Member since</p>
          <p className="text-white text-sm font-mono">
            {formatDate(user.created_at)}
          </p>
        </div>
      </Card>

      {/* ── Tab switcher ──────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-5 bg-gray-900/50 rounded-xl p-1 border border-gray-800/50">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-display font-semibold transition-all ${
              tab === t.id
                ? "bg-primary text-black shadow-glow-sm"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Ico name={t.icon} className="w-4 h-4" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* ── Profile info tab ──────────────────────────────────────────── */}
      {tab === "info" && (
        <Card>
          <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
            <Ico name="user" className="w-4 h-4 text-primary" /> Edit Profile
          </h3>

          {/* Avatar management row */}
          <div className="flex items-center gap-4 p-3 bg-gray-800/30 rounded-xl mb-4">
            <UserAvatar user={user} size={48} className="rounded-xl" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold">
                Profile Picture
              </p>
              <p className="text-gray-500 text-xs">
                JPG, PNG or WebP · max 5 MB
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                disabled={uploadingAvatar}
                onClick={() => avatarInputRef.current?.click()}
                className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/20 transition-all disabled:opacity-50"
              >
                {uploadingAvatar ? "Uploading…" : "Upload"}
              </button>
              {user.avatar_url && (
                <button
                  type="button"
                  disabled={uploadingAvatar}
                  onClick={removeAvatar}
                  className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-all disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          <form
            onSubmit={profileForm.handleSubmit(saveProfile)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="First Name"
                placeholder="John"
                error={profileForm.formState.errors.first_name?.message}
                {...profileForm.register("first_name")}
              />
              <Input
                label="Last Name"
                placeholder="Doe"
                error={profileForm.formState.errors.last_name?.message}
                {...profileForm.register("last_name")}
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              prefix={<Ico name="mail" className="w-4 h-4" />}
              error={profileForm.formState.errors.email?.message}
              {...profileForm.register("email")}
            />
            <Input
              label="Username"
              placeholder="johndoe"
              prefix={
                <span className="text-gray-500 text-sm font-mono leading-none">
                  @
                </span>
              }
              error={profileForm.formState.errors.username?.message}
              {...profileForm.register("username")}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={savingProfile}
                icon={<Ico name="save" className="w-4 h-4" />}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* ── Security tab ──────────────────────────────────────────────── */}
      {tab === "security" && (
        <Card>
          <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
            <Ico name="lock" className="w-4 h-4 text-primary" /> Change Password
          </h3>
          <form
            onSubmit={passwordForm.handleSubmit(savePassword)}
            className="flex flex-col gap-4"
          >
            <Input
              label="Current Password"
              type="password"
              placeholder="Current password"
              error={passwordForm.formState.errors.current_password?.message}
              {...passwordForm.register("current_password")}
            />
            <Input
              label="New Password"
              type="password"
              placeholder="Min 8 characters"
              error={passwordForm.formState.errors.password?.message}
              {...passwordForm.register("password")}
            />
            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Repeat new password"
              error={
                passwordForm.formState.errors.password_confirmation?.message
              }
              {...passwordForm.register("password_confirmation")}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={savingPassword}
                icon={<Ico name="lock" className="w-4 h-4" />}
              >
                Update Password
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* ── Wallets tab ───────────────────────────────────────────────── */}
      {tab === "wallets" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card glow>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm">Real Account</p>
                <p className="font-display font-bold text-primary text-3xl mt-0.5">
                  {formatUSD(wallets.real?.balance || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary/20 rounded-2xl flex items-center justify-center">
                <Ico name="wallet" className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href="/deposit"
                className={gs.btnPrimary + " text-sm flex-1 justify-center"}
              >
                Deposit
              </a>
              <a
                href="/withdraw"
                className={gs.btnSecondary + " text-sm flex-1 justify-center"}
              >
                Withdraw
              </a>
            </div>
          </Card>

          <Card className="border-blue-500/20">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm">Demo Account 🎮</p>
                <p className="font-display font-bold text-blue-400 text-3xl mt-0.5">
                  {formatUSD(wallets.demo?.balance || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                <Ico name="wallet" className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-gray-500 text-xs">
              Virtual funds for practice. Cannot be withdrawn.
            </p>
          </Card>
        </div>
      )}

      {user.admin_message && (
        <div className="mt-4">
          <Alert
            type="warning"
            title="Message from Admin"
            message={user.admin_message}
          />
        </div>
      )}
    </div>
  );
};

export default Profile;
